// Google Analytics 4 and Google Ads tracking functions for Next.js
import { trackEvent as trackMixpanelEvent } from './mixpanel';

declare global {
  interface Window {
    gtag: (command: string, targetId: string, config?: any) => void;
    dataLayer?: any[];
  }
}

// Configuration from environment variables
const GOOGLE_ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;

// Conversion labels for different actions
const CONVERSION_LABELS = {
  // start subscription trial
  TRAIL_STARTED_SRIPE_REDIRECT_SUCCESS: 'OJI3CILEkoYbEJeSqr1A',

  // click phone call demo
  DEMO_PHONE_CALL_NUMBER_CLICKED: 'mfTvCIXEkoYbEJeSqr1A',

  // registartion form submitted but stripe not yet completed
  REGISTRATION_FORM_SUBMITTED_NO_PAYMENT_YET: 'mBS0CIjEkoYbEJeSqr1A',

  // stripe form complete and trial has started
  TRIAL_START: 'trial_start_conversion',
};

// Check if gtag is available
const isGtagAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

// Enhanced conversion tracking for Google Ads
export const trackConversion = (
  conversionLabel: string, 
  value?: number, 
  transactionId?: string,
  currency: string = 'USD'
) => {
  if (!isGtagAvailable() || !GOOGLE_ADS_ID) {
    console.warn('gtag or Google Ads ID not available for conversion tracking');
    return;
  }

  try {
    const conversionData: any = {
      send_to: `${GOOGLE_ADS_ID}/${conversionLabel}`,
      value: value || 1.0,
      currency: currency
    };

    if (transactionId) {
      conversionData.transaction_id = transactionId;
    }

    window.gtag('event', 'conversion', conversionData);
    console.log('Conversion tracked:', { conversionLabel, value, transactionId, currency });
  } catch (error) {
    console.error('Error tracking conversion:', error);
  }
};

// Track subscription conversion (main goal)
export const trackSubscriptionConversion = (
  planName: string,
  actualValue: number,
  transactionId?: string
) => {
  // Use a fixed low value for Google Ads to avoid higher bidding costs
  const adsValue = 1; // Fixed $1 value for Google Ads
  
  // GA4 purchase event (can use actual value for internal analytics)
  if (isGtagAvailable()) {
    window.gtag('event', 'purchase', {
      transaction_id: transactionId || `subscription_${Date.now()}`,
      value: actualValue, // Keep actual value for GA4 analytics
      currency: 'USD',
      items: [{
        item_id: planName.toLowerCase().replace(/\s+/g, '_'),
        item_name: planName,
        category: 'subscription',
        quantity: 1,
        price: actualValue
      }]
    });
  }

  // Google Ads conversion (use fixed low value)
  trackConversion(CONVERSION_LABELS.TRAIL_STARTED_SRIPE_REDIRECT_SUCCESS, adsValue, transactionId);

  console.log('Subscription conversion tracked:', { planName, actualValue, adsValue, transactionId });
};

// Track phone number click (demo call intent)
export const trackPhoneClick = (phoneNumber: string, location: string) => {
  // GA4 event
  if (isGtagAvailable()) {
    window.gtag('event', 'phone_click', {
      event_category: 'engagement',
      event_label: phoneNumber,
      phone_number: phoneNumber,
      click_location: location
    });
  }

  // Google Ads conversion for demo call interest
  trackConversion(CONVERSION_LABELS.DEMO_PHONE_CALL_NUMBER_CLICKED, 1);

  // Mixpanel event
  trackMixpanelEvent('Demo Call Click', {
    phone_number: phoneNumber,
    location,
  });

  console.log('Phone click tracked:', { phoneNumber, location });
};

// Track registration completion
export const trackRegistration = (email: string, planName: string) => {
  // GA4 sign_up event
  if (isGtagAvailable()) {
    window.gtag('event', 'sign_up', {
      method: 'email',
      plan_name: planName,
      event_category: 'conversion'
    });
  }

  // Google Ads conversion
  trackConversion(CONVERSION_LABELS.REGISTRATION_FORM_SUBMITTED_NO_PAYMENT_YET, 1);

  // Mixpanel Sign Up event
  trackMixpanelEvent('Sign Up', {
    email,
    signup_method: 'email',
    plan: planName,
  });

  console.log('Registration tracked:', { email, planName });
};

// Track trial start
export const trackTrialStart = (planName: string) => {
  // GA4 event
  if (isGtagAvailable()) {
    window.gtag('event', 'begin_trial', {
      event_category: 'engagement',
      plan_name: planName
    });
  }

  // Google Ads conversion
  trackConversion(CONVERSION_LABELS.TRIAL_START, 1);

  console.log('Trial start tracked:', { planName });
};

// Track page views with enhanced data
export const trackPageView = (
  page_title: string,
  page_location?: string,
  custom_parameters?: Record<string, any>
) => {
  // Google Analytics tracking
  if (isGtagAvailable()) {
    try {
      const pageData: any = {
        page_title,
        ...custom_parameters
      };

      if (page_location) {
        pageData.page_location = page_location;
      }

      window.gtag('event', 'page_view', pageData);
      console.log('Page view tracked (GA):', pageData);
    } catch (error) {
      console.error('Error tracking page view (GA):', error);
    }
  }

  // Mixpanel tracking
  try {
    trackMixpanelEvent('Page View', {
      page_title,
      page_location: page_location || (typeof window !== 'undefined' ? window.location.href : undefined),
      ...custom_parameters
    });
    console.log('Page view tracked (Mixpanel):', { page_title, page_location, custom_parameters });
  } catch (error) {
    console.error('Error tracking page view (Mixpanel):', error);
  }
};

// Track form submissions
export const trackFormSubmission = (
  formName: string, 
  success: boolean = true,
  formData?: Record<string, any>
) => {
  if (!isGtagAvailable()) return;

  try {
    window.gtag('event', success ? 'form_submit' : 'form_error', {
      event_category: 'form',
      form_name: formName,
      success: success,
      ...formData
    });
    console.log('Form submission tracked:', { formName, success, formData });
  } catch (error) {
    console.error('Error tracking form submission:', error);
  }
};

// Track button clicks
export const trackButtonClick = (
  buttonName: string, 
  location?: string,
  additionalData?: Record<string, any>
) => {
  // Google Analytics tracking
  if (isGtagAvailable()) {
    try {
      window.gtag('event', 'click', {
        event_category: 'button',
        button_name: buttonName,
        click_location: location,
        ...additionalData
      });
      console.log('Button click tracked (GA):', { buttonName, location, additionalData });
    } catch (error) {
      console.error('Error tracking button click (GA):', error);
    }
  }

  // Mixpanel tracking
  try {
    trackMixpanelEvent('Button Click', {
      button_name: buttonName,
      click_location: location,
      ...additionalData
    });
    console.log('Button click tracked (Mixpanel):', { buttonName, location, additionalData });
  } catch (error) {
    console.error('Error tracking button click (Mixpanel):', error);
  }
};

// Track custom events
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number,
  customParameters?: Record<string, any>
) => {
  if (!isGtagAvailable()) return;

  try {
    const eventData: any = {
      event_category: category,
      ...customParameters
    };

    if (label) eventData.event_label = label;
    if (value !== undefined) eventData.value = value;

    window.gtag('event', action, eventData);
    console.log('Event tracked:', { action, category, label, value, customParameters });
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

// Track onboarding progress
export const trackOnboardingProgress = (progressPercentage: number) => {
  trackEvent('onboarding_progress', 'engagement', 'onboarding', Math.round(progressPercentage));
};

// Track onboarding completion
export const trackOnboardingComplete = () => {
  trackEvent('onboarding_complete', 'engagement', 'onboarding_finished');
};

// Track phone number purchase
export const trackPhoneNumberPurchase = (phoneNumber: string, cost: number) => {
  if (!isGtagAvailable()) return;

  try {
    window.gtag('event', 'purchase', {
      transaction_id: `phone_${Date.now()}`,
      value: cost,
      currency: 'USD',
      items: [{
        item_id: 'phone_number',
        item_name: 'Phone Number',
        category: 'phone_service',
        quantity: 1,
        price: cost
      }]
    });
    
    trackEvent('phone_number_purchased', 'conversion', phoneNumber, cost);
    
    // Mixpanel Purchase event
    trackMixpanelEvent('Purchase', {
      transaction_id: `phone_${Date.now()}`,
      revenue: cost,
      currency: 'USD',
      item_type: 'phone_number',
      phone_number: phoneNumber,
    });
    
    console.log('Phone number purchase tracked:', { phoneNumber, cost });
  } catch (error) {
    console.error('Error tracking phone number purchase:', error);
  }
};

// Track AI agent creation
export const trackAIAgentCreation = (agentType?: string) => {
  trackEvent('ai_agent_created', 'engagement', agentType || 'default');
};

// Track FAQ additions
export const trackFAQAdded = (faqCount: number) => {
  trackEvent('faq_added', 'content', 'faq_count', faqCount);
};

// Enhanced ecommerce tracking for Stripe redirect
export const trackStripeRedirect = (planName: string, planPrice: number) => {
  if (!isGtagAvailable()) return;

  try {
    window.gtag('event', 'begin_checkout', {
      currency: 'USD',
      value: planPrice,
      items: [{
        item_id: planName.toLowerCase().replace(/\s+/g, '_'),
        item_name: planName,
        category: 'subscription',
        quantity: 1,
        price: planPrice
      }]
    });
    console.log('Stripe redirect tracked:', { planName, planPrice });
  } catch (error) {
    console.error('Error tracking Stripe redirect:', error);
  }
};

// Track return from successful Stripe payment
export const trackStripeSuccess = (
  planName: string, 
  planPrice: number, 
  transactionId?: string,
  customerEmail?: string
) => {
  // This should be called when user returns from successful Stripe payment
  trackSubscriptionConversion(planName, planPrice, transactionId);
  
  // Additional GA4 events for successful payment
  if (isGtagAvailable()) {
    window.gtag('event', 'payment_success', {
      payment_method: 'stripe',
      plan_name: planName,
      customer_email: customerEmail,
      transaction_id: transactionId || `subscription_${Date.now()}`
    });
  }

  // Mixpanel Purchase event
  trackMixpanelEvent('Purchase', {
    transaction_id: transactionId || `subscription_${Date.now()}`,
    revenue: planPrice,
    currency: 'USD',
    plan_name: planName,
    email: customerEmail,
  });
};

// Export conversion labels for use in other components
export { CONVERSION_LABELS };

